import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Loader, FileText, X, Paperclip } from "lucide-react";
import "./Dashboard.css";
import { useAuth } from "../../context/AuthContext";
import { useNotification } from "../../context/NotificationContext";

export default function FileUploader({
  files,
  isUploading,
  disabled = false,
  onRemove,
  className = "",
  protectedFileKeys = [],
}) {
  const [isExpanded, setIsExpanded] = React.useState(false);

  // Normalize to array
  const fileList = Array.isArray(files) ? files : files ? [files] : [];

  if (fileList.length === 0 && !isUploading) return null;

  const showViewMore = fileList.length > 2;
  const displayedFiles = isExpanded ? fileList : fileList.slice(0, 2);

  return (
    <div
      className={`file-uploader-container ${className} ${isExpanded ? "is-expanded" : ""}`}
    >
      <div
        className={`file-uploader-list-wrapper ${isExpanded ? "is-expanded" : ""}`}
      >
        <AnimatePresence mode="popLayout">
          {isUploading && (
            <motion.div
              key="uploading"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              className="uploaded-file-display uploading"
            >
              <Loader size={14} className="spinning" />
              <span className="upload-status-text">Uploading...</span>
            </motion.div>
          )}

          {displayedFiles.map((file, index) => {
            const isProtected = protectedFileKeys.includes(
              file.s3Key || file.documentId,
            );
            return (
              <motion.div
                key={file.s3Key || file.fileName || index}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.9 }}
                layout
                className="uploaded-file-display"
              >
                <FileText size={14} className="file-icon" />

                <a
                  href={file.fileUrl || file.s3Url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="uploaded-file-link"
                  title={file.fileName}
                >
                  {file.fileName}
                </a>
                {!isProtected && (
                  <button
                    onClick={() => {
                      console.log(onRemove);
                      onRemove && onRemove(file);
                    }}
                    className="uploaded-file-remove"
                    disabled={disabled}
                    aria-label={`Remove ${file.fileName}`}
                  >
                    <X size={14} />
                  </button>
                )}
              </motion.div>
            );
          })}
        </AnimatePresence>
      </div>

      {showViewMore && (
        <motion.button
          className="view-more-files-btn"
          onClick={() => setIsExpanded(!isExpanded)}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
        >
          {isExpanded ? <>Show Less</> : <>+{fileList.length - 2} More Files</>}
        </motion.button>
      )}
    </div>
  );
}

export function FileUploadButton({
  fileInputRef,
  disabled = false,
  accept = ".pdf,.docx,.doc,.txt",
  onChange,
  multiple = true,
  maxAllowed,
  tooltipText,
}) {
  const isAtLimit = !!tooltipText;

  return (
    <>
      <input
        type="file"
        ref={fileInputRef}
        style={{ display: "none" }}
        accept={accept}
        disabled={disabled}
        multiple={multiple}
        onChange={(e) => {
          let files = Array.from(e.target.files || []);
          if (maxAllowed !== undefined && files.length > maxAllowed) {
            files = files.slice(0, maxAllowed);
          }
          if (files.length > 0 && onChange) {
            onChange(files);
          }
          e.target.value = ""; // Reset input
        }}
      />
      {isAtLimit ? (
        <div
          className="attach-btn-limit-wrapper"
          onMouseEnter={(e) => {
            const btn = e.currentTarget;
            const tip = btn.querySelector(".attach-btn-limit-tooltip");
            if (!tip) return;
            const rect = btn.getBoundingClientRect();
            tip.style.bottom = "auto";
            tip.style.top = `${rect.bottom + 5}px`;
            tip.style.right = `${window.innerWidth - rect.right}px`;
            tip.style.transform = "none";
          }}
        >
          <motion.button
            className="attach-btn-inside"
            disabled
          >
            <Paperclip size={18} />
          </motion.button>
          <div className="attach-btn-limit-tooltip">
            {tooltipText}
          </div>
        </div>
      ) : (
        <motion.button
          className="attach-btn-inside"
          onClick={(e) => {
            e.stopPropagation();
            fileInputRef.current?.click();
          }}
          whileHover={disabled ? {} : { scale: 1.1, rotate: 15 }}
          whileTap={disabled ? {} : { scale: 0.9 }}
          disabled={disabled}
          title={`Attach file${multiple ? "s" : ""} (PDF, DOCX, TXT)`}
        >
          <Paperclip size={18} />
        </motion.button>
      )}
    </>
  );
}
